import { warn } from "../../lib/log";
import { useState } from "react";
import { Users, Link2, Check } from "lucide-react";
import { DESTINATIONS } from "../../game/destinations";
import { buildBoardUrl } from "../../lib/leaderboard";
import { useLeaderboard } from "./useLeaderboard";

const BoardList = ({ entries }) => (
  <ol className="mt-3 space-y-1.5" data-testid="leaderboard-list">
    {entries.map((e, i) => (
      <li
        key={`${e.name}-${i}`}
        data-testid={`leaderboard-row-${i}`}
        className={`flex items-center gap-3 rounded-full px-3 py-1.5 ${e.is_me ? "bg-[#FFD166] text-[#14233D]" : "bg-[#EAF7FF] text-[#14233D]"}`}
      >
        <span className="font-display text-sm w-6 text-center">{i + 1}</span>
        <span className="text-sm font-bold truncate flex-1">{e.name}</span>
        <span className="font-display text-sm shrink-0">{DESTINATIONS[e.destination_index].name}</span>
      </li>
    ))}
  </ol>
);

const InviteButton = ({ code, owner }) => {
  const [copied, setCopied] = useState(false);
  const invite = async () => {
    const url = buildBoardUrl(code);
    const text = `🏴‍☠️ Entra nella classifica di «${owner}»: vince chi naviga più lontano!`;
    if (navigator.share) {
      await navigator.share({ text, url }).catch((e) => warn("Condivisione annullata", e));
      return;
    }
    await navigator.clipboard?.writeText(`${text}\n${url}`).catch((e) => warn("Copia non riuscita", e));
    setCopied(true);
  };
  return (
    <button
      type="button"
      onClick={invite}
      data-testid="leaderboard-invite-btn"
      className="mt-4 w-full rounded-full bg-[#55D7F3] text-[#14233D] font-display tracking-[0.08em] py-3 flex items-center justify-center gap-2 active:scale-95 transition-transform"
    >
      {copied ? <Check size={18} /> : <Link2 size={18} />}
      {copied ? "LINK COPIATO!" : "INVITA GLI AMICI"}
    </button>
  );
};

const JoinForm = ({ crewName, label, onSubmit, busy }) => {
  const [nick, setNick] = useState("");
  const name = (nick || crewName).trim();
  return (
    <div className="mt-3 flex gap-2">
      <input
        type="text"
        value={nick}
        maxLength={32}
        onChange={(e) => setNick(e.target.value)}
        placeholder={crewName || "Il tuo nickname"}
        data-testid="leaderboard-name-input"
        className="min-w-0 flex-1 rounded-full bg-[#EAF7FF] border-2 border-[#D0E4F3] focus:border-[#A989FF] outline-none px-4 py-2.5 text-sm font-semibold text-[#14233D] placeholder:text-[#506780]"
      />
      <button
        type="button"
        disabled={!name || busy}
        onClick={() => onSubmit(name)}
        data-testid="leaderboard-submit-btn"
        className="shrink-0 rounded-full bg-[#FFD166] text-[#14233D] font-display text-sm px-4 disabled:opacity-40 active:scale-95 transition-transform"
      >
        {label}
      </button>
    </div>
  );
};

export const LeaderboardPanel = ({ boardCode, destinationIndex, crewName, onBoardCreated }) => {
  const { board, joined, state, join, create } = useLeaderboard(boardCode, destinationIndex, onBoardCreated);
  const busy = state === "loading";
  return (
    <section className="mt-8 rounded-[26px] bg-white border border-[#D0E4F3] px-5 py-4" data-testid="leaderboard-panel">
      <p className="label-mono text-[#9A6A00] flex items-center gap-2">
        <Users size={14} /> Classifica amici
      </p>
      {board && <p className="text-sm font-semibold text-[#14233D] mt-2">Classifica di «{board.owner_name}»</p>}
      {!boardCode && (
        <p className="text-sm text-[#14233D]/85 mt-2 leading-snug">
          Crea una classifica e invita il tuo gruppo: vince chi naviga più lontano.
        </p>
      )}
      {!joined && (
        <JoinForm
          crewName={crewName}
          busy={busy}
          label={boardCode ? "ENTRA" : "CREA CLASSIFICA"}
          onSubmit={boardCode ? join : create}
        />
      )}
      {state === "error" && (
        <p className="text-xs text-[#C64BB4] mt-2" data-testid="leaderboard-error">Classifica non disponibile, riprova.</p>
      )}
      {board?.entries.length > 0 && <BoardList entries={board.entries} />}
      {board && <InviteButton code={board.code} owner={board.owner_name} />}
    </section>
  );
};
